import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A donut chart is a variation of a pie chart where the central part is removed, creating a
          donut-like appearance. It is used to represent proportions or percentages of categories
          within a whole, with each segment of the chart corresponding to a specific category. The
          central space of the donut can be utilized for additional information, such as total
          values, labels, or contextual insights, making it both functional and aesthetically
          appealing. Donut charts are especially effective for visualizing part-to-whole
          relationships in an engaging and concise manner.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Donut charts are effective tools for displaying proportions and part-to-whole
          relationships. Here are three key uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Proportions:</strong> Donut charts are ideal for showcasing how
            different categories contribute to a total. They provide an easy way to compare relative
            proportions, such as market share or budget allocation.
          </li>
          <li>
            <strong>Displaying Composition Breakdown:</strong> By using segments of a circle, donut
            charts are excellent for showing the breakdown of complex data sets into simpler parts,
            making it easier to understand distributions, like demographic data or survey results.
          </li>
          <li>
            <strong>Highlighting Key Insights:</strong> The center of a donut chart can be used to
            display an overall metric, such as the total sum or a percentage, making it easier to
            emphasize a key data point alongside the chart&apos;s segments, like progress toward a
            goal.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Donut charts are popular for visualizing proportions, but they have significant
          limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Comparing Sizes:</strong> Precise comparison between similar slice
            sizes is challenging, especially when values are close or numerous categories are
            displayed.
          </li>
          <li>
            <strong>Limited Data Representation:</strong> Donut charts are effective for
            part-to-whole relationships but cannot handle complex datasets or multiple dimensions
            effectively.
          </li>
          <li>
            <strong>Clutter with Multiple Categories:</strong> Adding too many slices leads to
            visual clutter, making the chart overwhelming and less effective in conveying insights.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is vital in donut charts to enhance clarity and highlight data
          distinctions. Using distinct and contrasting colors for each segment ensures that
          categories are easily distinguishable, especially in charts with many sections. Gradual
          color gradients can represent sequential or proportional data, emphasizing relationships
          between segments. Neutral tones or subdued colors can help reduce visual clutter when
          focusing on specific key segments. Thoughtful color choices guide the viewer&apos;s
          attention, making the chart both visually appealing and informative.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Donut charts are a visually appealing way to display proportions within a whole, but they
          present certain challenges that need to be considered for effective use. Here are the
          three most important challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Precision in Comparison:</strong> Donut charts rely on visual angles to
            represent data, which can make it difficult to compare segments with similar values. The
            human eye is not as accurate in judging angles, leading to imprecise comparisons between
            segments, especially when differences are subtle.
          </li>
          <li>
            <strong>Clutter with Too Many Categories:</strong> When the number of categories
            increases, donut charts can become overcrowded. More segments mean smaller portions,
            which may cause labels and data points to overlap, reducing readability and making it
            harder to extract meaningful insights.
          </li>
          <li>
            <strong>Lack of Clear Context for Values:</strong> Unlike bar or column charts, donut
            charts do not always offer a clear, easy way to display exact values for each segment.
            Without proper labeling or tooltips, viewers may struggle to understand the precise data
            behind the chart, especially if the segments are too small.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
